"use client";

import { useEffect, useState } from "react";
import { Navbar } from "./Navbar";
import { BottomNav } from "./BottomNav";
import { LayoutShell } from "./LayoutShell";
import { createClient } from "@/lib/supabase";
import type { UserRole } from "@/lib/auth";

interface AuthState {
  role: UserRole;
  userName?: string;
  userPhotoUrl?: string;
  unreadCount: number;
}

const LOGGED_OUT: AuthState = { role: null, unreadCount: 0 };

/**
 * Client-side auth chrome. The root layout must stay free of cookies()
 * so every page can render as static HTML (see CLAUDE.md gotchas); the
 * navbar discovers the session in the browser instead. Logged-out
 * visitors and crawlers make zero network calls. Logged-in users fetch
 * /api/me once per page view, which also refreshes their auth tokens.
 *
 * `footer` and `children` are passed through from the server layout, so
 * they remain server-rendered despite this being a client component.
 */
export function AppChrome({
  footer,
  children,
}: {
  footer: React.ReactNode;
  children: React.ReactNode;
}) {
  const [auth, setAuth] = useState<AuthState>(LOGGED_OUT);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const supabase = createClient();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session || cancelled) return; // logged out: nothing to fetch

      const res = await fetch("/api/me");
      if (!res.ok || cancelled) return;
      const me = (await res.json()) as AuthState;
      if (!cancelled) setAuth(me);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <LayoutShell
      navbar={
        <Navbar
          userRole={auth.role}
          unreadCount={auth.unreadCount}
          userName={auth.userName}
          userPhotoUrl={auth.userPhotoUrl}
        />
      }
      footer={footer}
      bottomNav={<BottomNav userRole={auth.role} unreadCount={auth.unreadCount} />}
      hasBottomNav={!!auth.role}
    >
      {children}
    </LayoutShell>
  );
}
